import React from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Layers, Clock, Users, UserCheck, Settings, UserPlus, UserCog, Copy } from "lucide-react";

type SidebarProps = {
  sidebarOpen: boolean;
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  role: string;
};

const Sidebar: React.FC<SidebarProps> = ({ sidebarOpen, setSidebarOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = (path: string) => location.pathname === path;
  const items = [
    { label: 'Platforms', id: 'platforms', icon: Layers, onClick: () => navigate('/access-management-dashboard'), path: '/access-management-dashboard', tooltip: 'Select and manage database platforms' },
    { label: 'Requests', id: 'requests', icon: Clock, path: '/access-request-queue', tooltip: 'View pending access requests' },
    { label: 'Security Groups', id: 'security-groups', icon: Users, path: '/security-groups', tooltip: 'Manage user security groups and permissions' },
    // { label: 'Securable Owner', id: 'securable-owner', icon: UserCog, path: '/securable-owner', tooltip: 'Assign and manage securable object owners' },
    { label: 'Securable Owner', id: 'securable-owner', icon: UserCog, path: '/securable-owner', tooltip: 'Manage securable owners' },
    // { label: 'Account Owner', id: 'account-owner', icon: UserCheck, path: '/account-owner', tooltip: 'Manage account ownership and stewardship' },
    { label: 'Service Account Owner', id: 'service-account-owner', icon: UserCheck, path: '/service-account-owner', tooltip: 'Assign and manage service account owners' },
    { label: 'Access Mirroring', id: 'access-mirroring', icon: Copy, path: '/access-mirroring', tooltip: 'Mirror user access permissions' },
  ];
  const footer = [
    { label: 'Settings', id: 'settings', icon: Settings, path: '/settings', tooltip: 'Administrative tools and settings' },
  ];
  return (
    <>
      {/* Overlay for mobile when sidebar is open */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <aside
        className={`fixed z-30 h-screen w-20 flex flex-col justify-between bg-[#32196c] text-white shadow-lg transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
      <div>
        <div className="p-4 flex items-center justify-center bg-white shadow-md relative">
          <img src="/assets/imgs/GACPOINT_Logo_transparent.png" alt="Logo" className="h-12 w-12 object-contain" />
          {/* Close button for mobile */}
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden absolute top-2 right-2 p-1 rounded-md hover:bg-gray-100 text-gray-600"
            aria-label="Close sidebar"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <nav className="mt-2 px-1 space-y-2">
          {items.map(item => {
            const Icon = item.icon; const active = isActive(item.path);
            const base = `group relative w-full h-14 flex items-center justify-center rounded-xl overflow-hidden ${active ? 'bg-white/20 shadow-inner' : 'hover:bg-blue-400/30'} `;
            return item.onClick ? (
              <button key={item.id} type="button" onClick={item.onClick} className={base} aria-label={item.label} title={item.tooltip}>
                <div className={`p-2 rounded-lg ${active ? 'bg-white/20' : ''}`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <span className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 opacity-0 group-hover:opacity-100 bg-gray-900 text-white text-xs px-2 py-1 rounded shadow whitespace-nowrap">{item.label}</span>
              </button>
            ) : (
              <NavLink key={item.id} to={item.path} className={() => base} aria-label={item.label} title={item.tooltip}>
                <div className={`p-2 rounded-lg ${active ? 'bg-white/20' : ''}`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <span className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 opacity-0 group-hover:opacity-100 bg-gray-900 text-white text-xs px-2 py-1 rounded shadow whitespace-nowrap">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
      </div>
      <div className="px-1 pb-3 space-y-2">
        {footer.map(item => {
          const Icon = item.icon; const active = isActive(item.path);
          const base = `group relative w-full h-14 flex items-center justify-center rounded-xl overflow-hidden ${active ? 'bg-white/20 shadow-inner' : 'hover:bg-blue-400/30'} `;
          return (
            <NavLink key={item.id} to={item.path} className={() => base} aria-label={item.label} title={item.tooltip}>
              <div className={`p-2 rounded-lg ${active ? 'bg-white/20' : ''}`}>
                <Icon className="h-6 w-6 text-white" />
              </div>
              <span className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 opacity-0 group-hover:opacity-100 bg-gray-900 text-white text-xs px-2 py-1 rounded shadow whitespace-nowrap">{item.label}</span>
            </NavLink>
          );
        })}
        <div className="text-center text-[9px] text-white/60">© {new Date().getFullYear()}</div>
      </div>
    </aside>
    </>
  );
};

export default Sidebar;

